
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import AsyncSelect from 'react-select/async';
import moment from 'moment'
import { DateRangePicker } from 'react-date-range';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

import useUtils from '../../../../utiles/useUtiles';
import Footer from '../../../components/Footer';
import { IMPORTED_CSV_STATUS } from '../../../../utiles/constants'

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { SelectInstance } from 'react-select';


const Report: React.FC = () => {

  const selectNetworkTypeRef = useRef<SelectInstance>(null);
  const selectNetworkRef = useRef<SelectInstance>(null);
  const inputDateRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>( false );
  const [networkList, setNetworkList] = useState<any[]>( [] );
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [focusedRange, setFocusedRange] = useState<any>([0, 0]);
  const [selectedRange, setSelectedRange] = useState<any>([{
      startDate: moment().subtract(6, 'days').toDate(),
      endDate: moment().toDate(),
      key: 'selection',
  }]);
  const [searchParams, setSearchParams] = useState<any>({
    csvType: urlParams.get('csvTypeId') && {label: '', value: urlParams.get('csvTypeId')} || null,
    network: urlParams.get('networkId') && {label: '', value: urlParams.get('networkId')} || null,
    searchText: null,
    startDate: urlParams.get('startDate') && moment(urlParams.get('startDate')).format("YYYY-MM-DD") || moment().subtract(6, 'days').format('YYYY-MM-DD'),
    endDate: urlParams.get('endDate') && moment(urlParams.get('endDate')).format("YYYY-MM-DD") || moment().format('YYYY-MM-DD'),
  })
  const { axiosRequest, baseUrl, toastInstance, loadingScreen } = useUtils()
  const axios = axiosRequest();
  const toast = toastInstance();

  const startDateUrlParam: string | null = urlParams.get('startDate');
  const endDateUrlParam: string | null = urlParams.get('endDate');
  const csvTypeIdUrlParam: string | null = urlParams.get('csvTypeId');
  const networkIdUrlParam: string | null = urlParams.get('networkId');

  useEffect( () => {
    if ( csvTypeIdUrlParam ) {
      fetchCsvTypesQueryString( csvTypeIdUrlParam )
    }

    setSelectedRange([{
        startDate: startDateUrlParam && moment(startDateUrlParam).toDate() || moment().subtract(6, 'days').toDate(),
        endDate: endDateUrlParam && moment(endDateUrlParam).toDate() || moment().toDate(),
        key: 'selection',
    }])

    fetchReport();
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [] );

  useEffect( () => {
    if ( searchParams['csvType'] ) {
        selectNetworkRef.current && selectNetworkRef.current.clearValue();
        fetchNetwork( '', searchParams['csvType']['value'] );
    }
  }, [ searchParams['csvType'] ] );

  useEffect(() => {
      setSearchParams({
        ...searchParams,
        startDate: selectedRange[0]['startDate'] && moment(selectedRange[0]['startDate']).format("YYYY-MM-DD") || null,
        endDate: selectedRange[0]['endDate'] && moment(selectedRange[0]['endDate']).format("YYYY-MM-DD") || null
      })
  }, [selectedRange])

  const fetchCsvTypesQueryString = async ( id: string ) => {
    const result = await fetchCsvTypes( { id } );
    if ( result.length ) {
      selectNetworkTypeRef.current && selectNetworkTypeRef.current.setValue(result[0], 'select-option')
    }

    if ( networkIdUrlParam ) {
      fetchNetworkQueryString(networkIdUrlParam, id)
    }
  }

  const fetchNetworkQueryString = async ( id: string, csvTypeId: string ) => {
    const result = await fetchNetwork({ id }, csvTypeId );
    if ( result.length ) {
      selectNetworkRef.current && selectNetworkRef.current.setValue(result[0], 'select-option')
    }
  }

  const fetchReport = async () => {
    setLoading( true );
    const queryString = [];
    if ( searchParams['csvType'] ) {
      queryString.push(`csvTypeId=${searchParams['csvType']['value']}`)
    }
    if ( searchParams['network'] ) {
      queryString.push(`networkId=${searchParams['network']['value']}`)
    }
    if ( searchParams['startDate'] ) {
      queryString.push(`startDate=${searchParams['startDate']}`)
    }
    if ( searchParams['endDate'] ) {
      queryString.push(`endDate=${searchParams['endDate']}`)
    }

    setUrlParams(queryString.join('&'))
    
		const request = await axios.get( `${baseUrl()}/import_csv/report?${queryString.length ? queryString.join('&') : ''}` );
    setLoading( false );
    const { error, result, message } = request.data;

		if ( error ) {
			toast.error( message );
		} else {
			setReportData( result );
		}
  };

  const csvTypeOptions = ( inputValue: string ): Promise<any[]> => {
    return new Promise<any[]>( async ( resolve ) => {
        const result: any[] = await fetchCsvTypes( {searchText: inputValue} );
        resolve( result );
    } );
  }
    
	const fetchCsvTypes = ( searchParams: any ): Promise<any[]> => {
      return new Promise<any[]>( async ( resolve ) => {
            const queryString = Object.keys(searchParams).map(key => `${key}=${searchParams[key]}`)
            const request = await axios.get( `${baseUrl()}/template_types?page=0&pageSize=5&${queryString.join('&')}` );
            const { error, result, message } = request.data;

            if ( error ) {
                toast.error( message );
                resolve( [] );
            } else {
                const data = result['rows'].map( (r: any) => ( {
                    label: r.name,
                    value: r.id 
                } ) );
                resolve( data );
            }
      } );
	};

  const onChangeNetwork = ( searchText: string ) => {
		if ( searchParams['csvType'] ) {
			fetchNetwork({searchText}, searchParams['csvType']['value']);
		}
	}

  const networkOptions = ( inputValue: string ): Promise<any[]> => {
    return new Promise<any[]>( async ( resolve ) => {
        if ( searchParams['csvType'] ) {
          const result: any[] = await fetchNetwork( {searchText: inputValue}, searchParams['csvType']['value'] );
          resolve( result );
        } else {
          resolve( [] );
        }
    });
  }
  const fetchNetwork = ( searchParams: any, templateType: string | null ): Promise<any[]> => {
      return new Promise<any[]>( async ( resolve ) => {
          var query = '';
          if ( searchParams['searchText'] ) {
              query += `&searchText=${searchParams['searchText']}`;
          } if ( searchParams['id'] ) {
            query += `&id=${searchParams['id']}`;
          } if ( templateType ) {
              query += `&csvTypeId=${templateType}`;
          }

          const request = await axios.get( `${baseUrl()}/template_type_networks?page=0&pageSize=5${query}` );
          const { error, result, message } = request.data;

          if ( error ) {
              toast.error( message );
              setNetworkList([]);
              resolve( [] );
          } else {
              const data = result['rows'].map( (r: any) => ( {
                  label: r.name,
                  value: r.id 
              } ) );
              setNetworkList( data );
              resolve( data );
          }
      } );
  };

  const resetFilter = () => {
    selectNetworkTypeRef.current && selectNetworkTypeRef.current.clearValue();
    selectNetworkRef.current && selectNetworkRef.current.clearValue();
    setSelectedRange([{
        startDate: null,
        endDate: null,
        key: 'selection',
    }])
  }

  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar);
  };

  const handleSelect = (ranges: any) => {
    setSelectedRange([ranges.selection]);
    if ( focusedRange[1] ) {
      setShowCalendar(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target as HTMLElement)) {
      setShowCalendar(false);
    }
  };

  const handleClick = (data: any, status: string) => {
    const queryString = [];
    queryString.push(`status=${status}`)
    if ( searchParams['csvType'] ) {
      queryString.push(`csvTypeId=${searchParams['csvType']['value']}`)
    } else if ( data['csv_types_id'] ) {
      queryString.push(`csvTypeId=${data['csv_types_id']}`)
    }
    if ( searchParams['network'] ) {
      queryString.push(`networkId=${searchParams['network']['value']}`)
    } else if ( data['networks_id'] ) {
      queryString.push(`networkId=${data['networks_id']}`)
    }
    if ( searchParams['startDate'] ) {
      queryString.push(`startDate=${searchParams['startDate']}`)
    }
    if ( searchParams['endDate'] ) {
      queryString.push(`endDate=${searchParams['endDate']}`)
    }

    navigate('/csv-tool/import-list?' + queryString.join('&'));
  }

  const staticRanges = [
    {
      label: 'Today',
      range: () => ({
        startDate: moment().toDate(),
        endDate: moment().toDate(),
      }),
      isSelected: (dateRange: any) => {
        const today = moment().toDate();
        return (
          dateRange.startDate.toDateString() === today.toDateString() &&
          dateRange.endDate.toDateString() === today.toDateString()
        );
      },
    },
    {
      label: 'Yesterday',
      range: () => {
        const yesterday = moment().subtract(1, 'days').toDate();
        return {
          startDate: yesterday,
          endDate: yesterday,
        };
      },
      isSelected: (dateRange: any) => {
        const yesterday = moment().subtract(1, 'days').toDate();
        return (
          dateRange.startDate.toDateString() === yesterday.toDateString() &&
          dateRange.endDate.toDateString() === yesterday.toDateString()
        );
      },
    },
    {
      label: 'Last 7 Days',
      range: () => ({
        startDate: moment().subtract(6, 'days').toDate(),
        endDate: moment().toDate(),
      }),
      isSelected: (dateRange: any) => {
        const last_7_days = moment().subtract(6, 'days').toDate();
        const today = moment().toDate();
        return moment(dateRange.endDate).diff(today, 'days') == 0 && moment(dateRange.startDate).diff(last_7_days, 'days') == 0;
      },
    },
    {
      label: 'Last 30 Days',
      range: () => ({
        startDate: moment().subtract(29, 'days').toDate(),
        endDate: moment().toDate(),
      }),
      isSelected: (dateRange: any) => {
        const last_30_days = moment().subtract(29, 'days').toDate();
        const today = moment().toDate();
        return moment(dateRange.endDate).diff(today, 'days') == 0 && moment(dateRange.startDate).diff(last_30_days, 'days') == 0;
      },
    },
    // Add more static ranges as needed
  ];

  return (
    <>
        <div className="container-fluid">
            <div className="row">
                <div className="col-sm-12">
                    <div className="main-container">
                      <div className="main-container-inner">
                        <div className="main-card">
                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                <h1 className='page-title-sm'>Imported CSV Report</h1>
                                {/* <button className='btn btn-md btn-custom-secondary' onClick={() => navigate('/csv-tool/import-csv')}>
                                    Import CSV
                                </button> */}
                            </div>
                            <div className='mb-4 row gap-10'>
                                <div className='col-md-5 select-dropdown'>
                                    <AsyncSelect 
                                        ref={selectNetworkTypeRef}
                                        placeholder="Select template type"
                                        cacheOptions 
                                        loadOptions={csvTypeOptions} 
                                        defaultOptions
                                        onChange={selectedOption => {
                                            setSearchParams((preValue: any) => ({
                                              ...preValue,
                                              network: null,
                                              csvType: selectedOption
                                            }));
                                            setNetworkList([]);
                                        }} 
                                    />
                                </div>
                                <div className='col-md-4 select-dropdown'>
                                    <AsyncSelect 
                                        ref={selectNetworkRef}
                                        placeholder="Select network"
                                        loadOptions={networkOptions} 
                                        defaultOptions={networkList}
                                        onInputChange={onChangeNetwork}
                                        onChange={selectedOption => {
                                            setSearchParams((preValue: any) => ({
                                              ...preValue,
                                              network: selectedOption
                                            }));
                                        }} 
                                    />
                                </div>
                                <div className='col-md-1 d-flex'>
                                    <button type="button" className="btn btn-md btn-custom-secondary" onClick={fetchReport}>Search</button> &nbsp;
                                    <button type="button" className="btn btn-md btn-custom-secondary" onClick={resetFilter}>Reset</button>
                                </div>
                                <div className="date-picker-container col-md-5" ref={calendarRef}>
                                  <div>
                                    <input
                                      ref={inputDateRef}
                                      className='form-control'
                                      type="text"
                                      value={ selectedRange[0].startDate && selectedRange[0].endDate && `${moment(selectedRange[0].startDate).format('MM/DD/YYYY')} - ${moment(selectedRange[0].endDate).format('MM/DD/YYYY')}` || ''}
                                      placeholder='Start Date - End Date'
                                      readOnly
                                    />
                                    <span className='pass-field-icon position-absolute' style={{right: '24px'}} onClick={handleCalendarToggle}>
                                      <FontAwesomeIcon
                                      color='#978f8f'
                                      icon={faCalendar}
                                      className="iconGray isPointer ml-2"
                                      />
                                    </span>
                                  </div>
                                  
                                  {
                                      showCalendar && (
                                        <div className="calendar-overlay">
                                          <DateRangePicker
                                            maxDate={new Date()}
                                            focusedRange={focusedRange}
                                            onRangeFocusChange={setFocusedRange}
                                            onChange={item => handleSelect(item)}
                                            months={2}
                                            ranges={selectedRange}
                                            staticRanges={staticRanges}
                                            direction="horizontal"
                                            showDateDisplay={true}
                                            dateDisplayFormat="MM/dd/yyyy"
                                          />
                                        </div>
                                      )
                                  }
                                </div>
                            </div>
                            
                            <div className='row'>
                              {
                                reportData && reportData.length && reportData.map((data: any, key: number) => (
                                    <Card key={key} className='mb-4 col-md-3'>
                                      <CardHeader
                                        title={ data['csv_type_name'] }
                                        subheader={ data['networks_name'] }
                                      />
                                      <CardContent>
                                        <Stack spacing={1}>
                                          <Stack direction="column" spacing={1}>
                                          {
                                            data && data['count'] && Object.keys(data['count']).map((_key: any) => (
                                              <Chip label={`${_key == 0 ? "Total Pending" : _key == 1 ? 'Total In Progress' : _key == 2 ? 'Total Completed' : 'Total Failed'}: ${data['count'][_key]}`} color={_key == 2 ? 'success' : 'error' } variant="outlined" key={_key} onClick={() => handleClick(data, _key)} />
                                            ))
                                          }
                                          </Stack>
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                )) || 
                                <Card className='text-center p-5 col-md-12'>
                                      <CardContent>
                                        Data Not Found
                                      </CardContent>
                                </Card>
                              }
                            </div>
                        </div>
                      </div>
                      <Footer />
                    </div>
                </div>
            </div>
        </div>
        {loadingScreen( loading )}
    </>
  );
}

export default Report
